import {
  faBrain,
  faCalculator,
  faMapLocationDot,
  faMoneyBillTrendUp,
  faScaleUnbalanced,
} from '@fortawesome/pro-duotone-svg-icons'

import { ImportedListing } from '../../_types/imported-listing'
import { ListingTag } from '../../_types/listing-tag'
import { QuickPassProfile } from '../../_types/quickpass-profile'
import { RenterInsight } from './types'

const OVER_BUDGET_FRACTION_THRESHOLD = 0.5

const computeAverage = (values: number[]): number | null => {
  if (values.length === 0) {
    return null
  }
  const total = values.reduce((sum, value) => sum + value, 0)
  return total / values.length
}

const collectPricesForTag = (
  listings: ImportedListing[],
  tags: Record<string, ListingTag>,
  targetTag: ListingTag,
): number[] => {
  const prices: number[] = []
  for (const listing of listings) {
    if ((tags[listing.id] ?? null) !== targetTag) {
      continue
    }
    if (typeof listing.priceMonthly === 'number') {
      prices.push(listing.priceMonthly)
    }
  }
  return prices
}

/**
 * Builds a list of derived renter insights from their preferences,
 * imported listings, and tagging history. Returned in importance order
 * — first one wins when limited.
 *
 * @param {QuickPassProfile | null} profile - the renter profile
 * @param {ImportedListing[]} listings - the renter's tracked listings
 * @param {Record<string, ListingTag>} tags - the renter's tag map
 * @returns {RenterInsight[]} the resolved insights, importance-sorted
 */
const buildRenterInsights = (
  profile: QuickPassProfile | null,
  listings: ImportedListing[],
  tags: Record<string, ListingTag>,
): RenterInsight[] => {
  const insights: RenterInsight[] = []

  if (profile === null) {
    return insights
  }

  const preferences = profile.unitPreferences
  const priceMax = preferences.priceMax

  if (priceMax !== null && listings.length > 0) {
    const overBudget = listings.filter(
      listing =>
        typeof listing.priceMonthly === 'number' &&
        listing.priceMonthly > priceMax,
    )

    const fraction = overBudget.length / listings.length
    if (fraction >= OVER_BUDGET_FRACTION_THRESHOLD) {
      insights.push({
        id: 'majority-over-budget',
        severity: 'nudge',
        glyph: faScaleUnbalanced,
        title: 'Most of your shortlist is over budget',
        body: `${overBudget.length} of ${listings.length} tracked places are over your $${priceMax.toLocaleString('en-CA')} max. Consider raising the cap, dropping some, or hunting more aggressively.`,
        callToAction: 'Tune budget',
        callToActionPath: '/profile',
      })
    }
  }

  const yesPrices = collectPricesForTag(listings, tags, 'yes')
  const noPrices = collectPricesForTag(listings, tags, 'no')
  const avgYes = computeAverage(yesPrices)
  const avgNo = computeAverage(noPrices)

  if (avgYes !== null && avgNo !== null && yesPrices.length >= 2) {
    if (avgYes > avgNo * 1.05) {
      insights.push({
        id: 'paying-up-for-yes',
        severity: 'nudge',
        glyph: faMoneyBillTrendUp,
        title: "You're drawn to the pricier ones",
        body: `Your "yes" picks average $${Math.round(avgYes).toLocaleString('en-CA')}/mo — about $${Math.round(avgYes - avgNo).toLocaleString('en-CA')} more than your "no" piles. Worth it?`,
        callToAction: null,
        callToActionPath: null,
      })
    }

    if (avgYes < avgNo * 0.95) {
      insights.push({
        id: 'savvy-on-yes',
        severity: 'celebrate',
        glyph: faBrain,
        title: "You're tagging smart",
        body: `Your "yes" picks average $${Math.round(avgYes).toLocaleString('en-CA')}/mo — about $${Math.round(avgNo - avgYes).toLocaleString('en-CA')} less than your "no" piles. Picking value over flash.`,
        callToAction: null,
        callToActionPath: null,
      })
    }
  }

  if (preferences.cities.length > 0 && listings.length > 0) {
    const lowercaseCities = new Set(
      preferences.cities.map(city => city.toLowerCase()),
    )
    const offCity = listings.filter(
      listing =>
        listing.city !== '' &&
        lowercaseCities.has(listing.city.toLowerCase()) === false,
    )

    if (offCity.length >= 2 && offCity.length / listings.length >= 0.3) {
      const cityNames = Array.from(
        new Set(offCity.map(listing => listing.city)),
      ).slice(0, 3)

      insights.push({
        id: 'imports-outside-cities',
        severity: 'info',
        glyph: faMapLocationDot,
        title: "You're looking outside your cities",
        body: `${offCity.length} listings are in cities not on your profile (${cityNames.join(', ')}). Add them to your preferences if you're open?`,
        callToAction: 'Update cities',
        callToActionPath: '/profile',
      })
    }
  }

  if (profile.monthlyTakeHomeIncome === null && listings.length >= 3) {
    insights.push({
      id: 'add-income-for-affordability',
      severity: 'info',
      glyph: faCalculator,
      title: 'Want a budget read on each listing?',
      body: "Add your monthly take-home in the profile to see how each listing's rent fits — under 30% is comfortable, 30-50% is tight, 50%+ is overspend.",
      callToAction: 'Add income',
      callToActionPath: '/profile',
    })
  }

  return insights
}

export { buildRenterInsights }
